import React, { useState } from 'react';
import AdminShell from '../../../components/admin/AdminShell';
import { useCampaigns, usePauseCampaign, useResumeCampaign } from '../../../hooks/useMarketingQueries';
import { useFilters } from '../../../hooks/useFilters';
import { useDebounce } from '../../../hooks/useDebounce';
import { exportCampaignsToCSV } from '../../../lib/csvExport';
import { toast } from 'sonner';
import { Plus, AlertCircle, Play, Pause, Download, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';

export default function CampaignsPage() {
  const navigate = useNavigate();
  const { filters, setFilter } = useFilters({
    search: '',
    status: 'all',
    channel: 'all',
    sort: 'created_desc',
  });
  
  const debouncedSearch = useDebounce(filters.search, 350);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState(null); // 'pause' or 'resume'
  
  const pauseCampaign = usePauseCampaign();
  const resumeCampaign = useResumeCampaign();
  
  // Query params for API
  const queryParams = {
    search: debouncedSearch || undefined,
    status: filters.status === 'all' ? undefined : filters.status,
    channel: filters.channel === 'all' ? undefined : filters.channel,
  };
  
  const { data: campaigns = [], isLoading, error } = useCampaigns(queryParams);
  
  // Sort campaigns client-side
  const sortedCampaigns = React.useMemo(() => {
    const sorted = [...campaigns];
    const [field, direction] = filters.sort.split('_');
    
    sorted.sort((a, b) => {
      let aVal, bVal;
      if (field === 'created') {
        aVal = new Date(a.created_at);
        bVal = new Date(b.created_at);
      } else if (field === 'name') {
        aVal = a.name?.toLowerCase() || '';
        bVal = b.name?.toLowerCase() || '';
      } else if (field === 'sent') {
        aVal = a.total_sent || 0;
        bVal = b.total_sent || 0;
      }
      
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return sorted;
  }, [campaigns, filters.sort]);
  
  const handleExport = () => {
    exportCampaignsToCSV(sortedCampaigns, 'campaigns.csv');
    toast.success('Campaigns exported to CSV');
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.length === sortedCampaigns.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sortedCampaigns.map(c => c._id));
    }
  };
  
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };
  
  const handleBulkAction = () => {
    const selectedCampaigns = sortedCampaigns.filter(c => selectedIds.includes(c._id));
    
    if (bulkAction === 'pause') {
      Promise.all(
        selectedCampaigns
          .filter(c => c.status === 'running')
          .map(c => pauseCampaign.mutateAsync(c._id))
      )
        .then(() => {
          toast.success(`${selectedIds.length} campaign(s) paused`);
          setSelectedIds([]);
          setBulkAction(null);
        })
        .catch(() => toast.error('Failed to pause some campaigns'));
    } else if (bulkAction === 'resume') {
      Promise.all(
        selectedCampaigns
          .filter(c => c.status === 'paused')
          .map(c => resumeCampaign.mutateAsync(c._id))
      )
        .then(() => {
          toast.success(`${selectedIds.length} campaign(s) resumed`);
          setSelectedIds([]);
          setBulkAction(null);
        })
        .catch(() => toast.error('Failed to resume some campaigns'));
    }
  };
  
  const canPause = () => {
    const selected = sortedCampaigns.filter(c => selectedIds.includes(c._id));
    return selected.some(c => c.status === 'running');
  };
  
  const canResume = () => {
    const selected = sortedCampaigns.filter(c => selectedIds.includes(c._id));
    return selected.some(c => c.status === 'paused');
  };
  
  const statusBadge = (status) => {
    const styles = {
      draft: 'bg-[rgba(185,185,185,0.12)] border-[rgba(185,185,185,0.22)] text-[#B9B9B9]',
      scheduled: 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]',
      running: 'bg-[rgba(61,220,151,0.12)] border-[rgba(61,220,151,0.22)] text-[#BFF3D2]',
      paused: 'bg-[rgba(241,200,75,0.12)] border-[rgba(241,200,75,0.22)] text-[#F1E2B8]',
      completed: 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]',
      failed: 'bg-[rgba(255,77,77,0.12)] border-[rgba(255,77,77,0.22)] text-[#FFD7D7]'
    };
    return (
      <Badge className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs border ${styles[status] || styles.draft}`} data-testid="campaign-status-badge">
        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
        {status}
      </Badge>
    );
  };
  
  const channelBadge = (channel) => {
    const styles = {
      sms: 'bg-[rgba(42,166,161,0.12)] border-[rgba(42,166,161,0.22)] text-[#A0E7E5]',
      whatsapp: 'bg-[rgba(61,220,151,0.12)] border-[rgba(61,220,151,0.22)] text-[#BFF3D2]',
    };
    return (
      <Badge className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs border ${styles[channel] || styles.sms}`}>
        {channel?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <AdminShell 
      title="Campaigns" 
      actions={
        <div className="flex items-center gap-2">
          {sortedCampaigns.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 gap-2"
              data-testid="campaigns-export-button"
              aria-label="Export campaigns to CSV"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
          )}
          <Button 
            onClick={() => navigate('/admin/marketing/campaigns/new')}
            className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
            data-testid="campaigns-new-button"
            aria-label="Create new campaign"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">New Campaign</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B9B9B9]" size={16} aria-hidden="true" />
            <Input
              placeholder="Search campaigns..."
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
              className="pl-10 bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]"
              data-testid="campaigns-search-input"
              aria-label="Search campaigns"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilter('status', v)}>
            <SelectTrigger 
              className="w-full sm:w-[200px] bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]" 
              data-testid="campaigns-status-filter"
              aria-label="Filter by status"
            >
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.channel} onValueChange={(v) => setFilter('channel', v)}>
            <SelectTrigger 
              className="w-full sm:w-[180px] bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]"
              aria-label="Filter by channel"
            >
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.sort} onValueChange={(v) => setFilter('sort', v)}>
            <SelectTrigger 
              className="w-full sm:w-[180px] bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]"
              aria-label="Sort campaigns"
            >
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">Latest Created</SelectItem>
              <SelectItem value="created_asc">Oldest Created</SelectItem>
              <SelectItem value="sent_desc">Most Sent</SelectItem>
              <SelectItem value="sent_asc">Least Sent</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Bulk Actions Toolbar */}
        {selectedIds.length > 0 && (
          <div className="bg-[rgba(212,168,67,0.08)] border border-[rgba(212,168,67,0.22)] rounded-lg p-3 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-[#F2F2F2]">
              {selectedIds.length} campaign{selectedIds.length > 1 ? 's' : ''} selected
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkAction('pause')}
                disabled={!canPause()}
                className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:bg-[rgba(255,255,255,0.05)] gap-2 disabled:opacity-50"
                data-testid="bulk-pause-button"
                aria-label="Pause selected campaigns"
              >
                <Pause size={14} />
                Pause
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkAction('resume')}
                disabled={!canResume()}
                className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:bg-[rgba(255,255,255,0.05)] gap-2 disabled:opacity-50"
                data-testid="bulk-resume-button"
                aria-label="Resume selected campaigns"
              >
                <Play size={14} />
                Resume
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="surface-card card-highlight rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-[#2A2A2A]">
                <tr>
                  <th className="w-12 px-5 py-3">
                    <Checkbox
                      checked={sortedCampaigns.length > 0 && selectedIds.length === sortedCampaigns.length}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all campaigns"
                    />
                  </th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Campaign</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Channel</th>
                  <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Audience</th>
                  <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Sent</th>
                  <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Delivered</th>
                  <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Failed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E1E1E]">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3">
                        <div className="h-4 w-4 bg-[#202020] rounded animate-pulse" />
                      </td>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} className="px-5 py-3">
                          <div className="h-4 bg-[#202020] rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center">
                      <AlertCircle size={40} className="mx-auto mb-3 text-[#FF4D4D]" />
                      <p className="text-[#B9B9B9] text-sm">Failed to load campaigns</p>
                    </td>
                  </tr>
                ) : sortedCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center">
                      <AlertCircle size={40} className="mx-auto mb-3 text-[#6F6F6F]" />
                      <p className="text-[#B9B9B9] text-sm mb-4">No campaigns yet</p>
                      <Button
                        onClick={() => navigate('/admin/marketing/campaigns/new')}
                        className="bg-[#D4A843] text-black hover:brightness-110"
                      >Create your first campaign</Button>
                    </td>
                  </tr>
                ) : (
                  sortedCampaigns.map(campaign => (
                    <tr 
                      key={campaign._id} 
                      className={`hover:bg-[#161616] transition-colors cursor-pointer ${selectedIds.includes(campaign._id) ? 'bg-[rgba(212,168,67,0.05)]' : ''}`}
                      onClick={() => navigate(`/admin/marketing/campaigns/${campaign._id}`)}
                      data-testid="campaign-row"
                    >
                      <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(campaign._id)}
                          onCheckedChange={() => toggleSelect(campaign._id)}
                          aria-label={`Select ${campaign.name}`}
                        />
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-sm text-[#F2F2F2] font-medium">{campaign.name}</p>
                        <p className="text-xs text-[#6F6F6F]">{new Date(campaign.created_at).toLocaleDateString()}</p>
                      </td>
                      <td className="px-5 py-3">{statusBadge(campaign.status)}</td>
                      <td className="px-5 py-3">{channelBadge(campaign.channel)}</td>
                      <td className="px-5 py-3 text-right">
                        <p className="text-sm text-[#F2F2F2] font-mono">{campaign.total_recipients || 0}</p>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <p className="text-sm text-[#F2F2F2] font-mono">{campaign.total_sent || 0}</p>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <p className="text-sm text-[#F2F2F2] font-mono">{campaign.total_delivered || 0}</p>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <p className="text-sm text-[#FF4D4D] font-mono">{campaign.total_failed || 0}</p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Bulk Action Confirmation */}
      <AlertDialog open={!!bulkAction} onOpenChange={() => setBulkAction(null)}>
        <AlertDialogContent className="bg-[#111111] border-[#2A2A2A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F2F2F2]">
              {bulkAction === 'pause' ? 'Pause' : 'Resume'} Campaigns
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#B9B9B9]">
              Are you sure you want to {bulkAction} {selectedIds.length} selected campaign{selectedIds.length > 1 ? 's' : ''}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkAction} className="bg-[#D4A843] text-black hover:brightness-110">
              {bulkAction === 'pause' ? 'Pause' : 'Resume'} All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
